import {useState, useEffect, type ReactNode} from 'react'
import {Input} from '@/components/ui/input'
import {DetailField} from '@/components/shared/DetailField'
import {Stack} from '@/components/shared/Stack'
import {ErrorText} from '@/components/shared/ErrorText'

interface InlineNumberFieldProps {
    label: ReactNode
    value: number
    canEdit: boolean
    onCommit: (newValue: number) => Promise<void>
    min?: number
    max?: number
    suffix?: string
    fullWidth?: boolean
    testId?: string
    'aria-label'?: string
}

export function InlineNumberField({
    label,
    value,
    canEdit,
    onCommit,
    min,
    max,
    suffix,
    fullWidth,
    testId,
    'aria-label': ariaLabel,
}: InlineNumberFieldProps) {
    const [localValue, setLocalValue] = useState(String(value))
    const [error, setError] = useState<string | null>(null)
    const [pending, setPending] = useState(false)

    useEffect(() => {
        setLocalValue(String(value))
    }, [value])

    if (!canEdit) {
        return (
            <DetailField
                label={label}
                value={suffix ? `${value}${suffix}` : String(value)}
                fullWidth={fullWidth}
            />
        )
    }

    const handleBlur = async () => {
        const parsed = parseInt(localValue, 10)
        if (Number.isNaN(parsed)) {
            setLocalValue(String(value))
            setError(null)
            return
        }
        if (parsed === value) {
            setError(null)
            return
        }
        setError(null)
        setPending(true)
        try {
            await onCommit(parsed)
        } catch (err) {
            setLocalValue(String(value))
            const msg = err instanceof Error ? err.message : String(err)
            setError(msg)
        } finally {
            setPending(false)
        }
    }

    return (
        <DetailField
            label={label}
            fullWidth={fullWidth}
            value={
                <Stack gap="xs">
                    <Input
                        type="number"
                        min={min}
                        max={max}
                        value={localValue}
                        disabled={pending}
                        data-testid={testId}
                        aria-label={ariaLabel}
                        onChange={e => setLocalValue(e.target.value)}
                        onBlur={handleBlur}
                    />
                    {error && (
                        <ErrorText data-testid={testId ? `${testId}-error` : undefined}>
                            {error}
                        </ErrorText>
                    )}
                </Stack>
            }
        />
    )
}
